import { useState, useEffect, useRef } from "react";
import { Check, X, Search } from "lucide-react";

/**
 * Multi-select with search. Props:
 *  - options: [{ value, label, sub? }]
 *  - value: string[]
 *  - onChange: (string[]) => void
 *  - placeholder
 */
const MultiSelect = ({ options = [], value = [], onChange, placeholder = "Auswählen…" }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter(x => x !== v));
    else onChange([...value, v]);
  };
  const remove = (v) => onChange(value.filter(x => x !== v));

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase()) ||
    (o.sub || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen(!open)}
        className="min-h-[42px] w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm cursor-pointer flex flex-wrap items-center gap-1.5"
        data-testid="multi-select-trigger"
      >
        {value.length === 0 && <span className="text-muted-foreground">{placeholder}</span>}
        {value.map(v => {
          const o = options.find(o => o.value === v);
          return (
            <span key={v} className="inline-flex items-center gap-1 bg-primary/15 border border-primary/30 text-primary rounded-full px-2.5 py-0.5 text-xs">
              {o?.label || v}
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(v); }} className="hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-border rounded-md shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Suchen…"
              data-testid="multi-select-search"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Keine Ergebnisse</div>}
            {filtered.map(o => (
              <button
                type="button"
                key={o.value}
                onClick={() => toggle(o.value)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/60 text-left text-sm"
              >
                <div>
                  <div>{o.label}</div>
                  {o.sub && <div className="text-xs text-muted-foreground">{o.sub}</div>}
                </div>
                {value.includes(o.value) && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
